import React, { useRef, useState, useEffect } from 'react';
import useAuthState from "@/states/auth.state";
import useTenantState from "@/states/tenant.state";
import { useParams } from "react-router-dom";
import { swalSuccessDefault } from "@/components/ui/Swal";
import { encodeHtmlToBase64DataUrl, injectParametersIntoHtml } from './RenderCode/utils/base64Utils';

interface IframeCustomForm2Props {
  props: any;
}

const IframeCustomForm: React.FC<IframeCustomForm2Props> = ({ props }) => {
  const { item } = props;
  const { currentTenant } = useTenantState();
  const params = useParams();
  const [token, setToken] = useState<string | null>(null);

  const [base64Url, setBase64Url] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch token on mount
  useEffect(() => {
    const fetchToken = async () => {
      const authToken = await useAuthState.getState().getToken();
      setToken(authToken);
    };
    fetchToken();
  }, []);

  const url = props.item.url;
  // inject other params to iframe from authState
  const x_active_tenant = currentTenant?.slug;
  const probis = params.processKey;
  const x_task_id = params.id;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const finalUrl = `${url}`;

  // Function to fetch content and convert to base64
  const fetchAndConvertToBase64 = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(finalUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      let html = await response.text(); 
      
      // Prepare parameters for injection
      const params = {
        'token': token || '',
        'x-active-tenant': x_active_tenant || '',
        'probis': probis || '',
        'task_id': x_task_id || '',
      };
      
      // Inject parameters into HTML content
      html = injectParametersIntoHtml(html, params);
      
      // Convert to base64 data URL (safely handle Unicode characters)
      const dataUrl = encodeHtmlToBase64DataUrl(html);
      
      setBase64Url(dataUrl);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error('Failed to fetch iframe content:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Load content on mount and when dependencies change
  useEffect(() => {
    fetchAndConvertToBase64();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalUrl]);

  // terima event dari iframe formSubmissionResult
  const handleFormSubmissionResult = (event: MessageEvent) => {
    if (event.data.type === 'formSubmissionResult') {
      swalSuccessDefault.fire({
        title: 'Form Submitted',
        text: 'The form has been successfully submitted.',
        icon: 'success',
      }).then((resultData) => {
        console.log('resultData', resultData);
        if (resultData.isConfirmed) {
          // redirect to another page
          globalThis.location.href = '/' + currentTenant?.slug + '/my-request/active';
        } else {
          // reload iframe by refetching content
          fetchAndConvertToBase64();
        }
      });
    }
  };

  React.useEffect(() => {
    globalThis.addEventListener('message', handleFormSubmissionResult);
    return () => {
      globalThis.removeEventListener('message', handleFormSubmissionResult);
    };
  }, [currentTenant?.slug]);

  // Show loading state
  if (loading) {
    return (
      <div 
        style={{ 
          width: item.width ?? "100%", 
          height: item.height ?? "500px",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          background: '#f5f5f5'
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '10px' }}>Loading form...</div>
          <div style={{ 
            width: '20px', 
            height: '20px', 
            border: '2px solid #ccc', 
            borderTop: '2px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }} />
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div 
        style={{ 
          width: item.width ?? "100%", 
          height: item.height ?? "500px",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #ff6b6b',
          borderRadius: '4px',
          background: '#ffe0e0',
          color: '#d63031'
        }}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>Failed to load form</div>
          <div style={{ fontSize: '14px', marginBottom: '15px' }}>{error}</div>
          <button 
            onClick={fetchAndConvertToBase64}
            style={{
              padding: '8px 16px',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <iframe 
        src={base64Url} 
        ref={iframeRef} 
        width={item.width ?? "100%"} 
        height={item.height ?? "500px"}
        title="Custom Form Iframe"
        style={{
          border: '1px solid #e0e0e0',
          borderRadius: '4px'
        }}
      />
    </>
  );
};

export default IframeCustomForm;